import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import MuiTable from '@mui/material/Table';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTablePagination from '@mui/material/TablePagination';
import { TableBody, TableHead } from 'Components';
import { design } from 'utils';

const Root = styled('div')(() => ({
  width: '100%',
  border: `2px solid ${design.colors.secondary}`,
  borderRadius: design.radius[1],
  overflow: 'hidden',
  '& tbody .MuiTableRow-root': {
    '&:nth-child(2n)': {
      backgroundColor: design.colors.white,
    },
    '&:hover': {
      backgroundColor: design.colors.tertiary,
    },
  },
  '& + &': {
    marginTop: design.spacing[4],
  },
  '& .MuiTableCell-root': {
    fontSize: design.fontSizes[2],
    whiteSpace: 'nowrap',
    padding: `${design.spacing[1]}`,
  },
  '& th': {
    fontWeight: 'bold',
    color: design.colors.white,
    backgroundColor: design.colors.secondary,
  },
  '& th .MuiTableSortLabel-root.Mui-active': {
    color: design.colors.tertiary,
  },
  '& th .MuiTableSortLabel-root.Mui-active.MuiTableSortLabel-root.Mui-active svg':
    {
      color: design.colors.tertiary,
    },
  '& .MuiButtonBase-root.MuiTableSortLabel-root:hover': {
    color: design.colors.tertiary,
  },
}));

const rowsPerPageOptions = [10, 20, 50, 100];

const Table = ({
  defaultOrder,
  defaultOrderBy,
  defaultRowsPerPage,
  enablePagination,
  enableSelection,
  headCells,
  rowLinks = {},
  rows,
  size = 'medium',
  tableTitle,
}) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    if (defaultOrder) {
      setOrder(defaultOrder);
    }
    if (defaultOrderBy) {
      setOrderBy(defaultOrderBy);
    }
    if (defaultRowsPerPage) {
      setRowsPerPage(defaultRowsPerPage);
    }
  }, [defaultOrder, defaultOrderBy, defaultRowsPerPage]);

  // const classes = useStyles();

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rowCount = rows.length;

  return (
    <Root>
      <MuiTableContainer>
        <MuiTable
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={size}
          aria-label="enhanced table"
        >
          <TableHead
            headCells={headCells}
            numSelected={selected.length}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
            order={order}
            orderBy={orderBy}
            rowCount={rowCount}
          />
          <TableBody
            enableSelection={enableSelection}
            order={order}
            orderBy={orderBy}
            page={page}
            rowLinks={rowLinks}
            rowsPerPage={rowsPerPage}
            selected={selected}
            setSelected={setSelected}
            rows={rows}
          />
        </MuiTable>
      </MuiTableContainer>
      {enablePagination && (
        <MuiTablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rowCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Root>
  );
};

Table.propTypes = {
  defaultOrder: PropTypes.string,
  defaultOrderBy: PropTypes.string,
  defaultRowsPerPage: PropTypes.oneOf(rowsPerPageOptions),
  enablePagination: PropTypes.bool,
  enableSelection: PropTypes.bool,
  headCells: PropTypes.array.isRequired,
  rowLinks: PropTypes.object,
  rows: PropTypes.array.isRequired,
  size: PropTypes.string,
  tableTitle: PropTypes.string,
};

export default Table;
